// 011 US2 (P-ORD-2, P-ORD-3, P-ORD-6; FR-010..FR-030): one order — status, the timeline, the delivery
// code exactly when the server includes it, the driver with Call, the items per child, totals, the
// address, linked orders, and the notes and refund state of a failed or cancelled order. While it is
// Placed, Packed or On the way and the app is in the foreground, it is re-read every 30 seconds.
import { Link } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  AppState,
  Linking,
  ScrollView,
  StyleSheet,
  Text as RNText,
  View,
} from "react-native";

import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";
import { addressLabelText } from "@/profile/viewModels";
import { classSectionText } from "@/shop/useShopper";
import { formatPaise } from "@/shop/format";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";

import {
  istDate,
  istDateTime,
  LIVE_STATUSES,
  methodLabel,
  ORDERS_COPY as C,
  STATUS_LABELS,
  timelineSteps,
} from "./copy";
import { useOrdersClient } from "./ordersApi";

export const ORDER_REFRESH_MS = 30_000;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1, backgroundColor: t.color.background },
    asWritten: { textTransform: "none" },
    page: {
      width: "100%",
      maxWidth: 720,
      alignSelf: "center",
      padding: t.space.lg,
      gap: t.space.md,
      paddingBottom: t.space.x3l,
    },
    card: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.surface,
      padding: t.space.lg,
      gap: t.space.sm,
    },
    codeCard: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.tileAmber,
      padding: t.space.lg,
      gap: t.space.xs,
    },
    onCode: { color: t.color.onTileAmber },
    code: { color: t.color.onTileAmber },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: t.space.md,
    },
    step: { flexDirection: "row", alignItems: "center", gap: t.space.sm },
    dot: {
      width: 12,
      height: 12,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.border,
    },
    dotDone: { backgroundColor: t.color.success },
    dotCurrent: { backgroundColor: t.color.primary },
    grow: { flex: 1 },
  }),
);

export function OrderDetailScreen({ orderId }: { orderId: string }) {
  const styles = useStyles();
  const client = useOrdersClient();
  const [order, setOrder] = useState<Api["ParentOrder"] | null>(null);
  const [error, setError] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    try {
      setOrder(await client.getOrder(orderId));
      setError(null);
    } catch (e) {
      setError(e as AppError);
    }
  }, [client, orderId]);

  useEffect(() => {
    let live = true;
    client.getOrder(orderId).then(
      (o) => {
        if (live) setOrder(o);
      },
      (e: AppError) => {
        if (live) setError(e);
      },
    );
    return () => {
      live = false;
    };
  }, [client, orderId]);

  const polling = order !== null && LIVE_STATUSES.includes(order.status);
  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(() => {
      if (AppState.currentState === "active") void load();
    }, ORDER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [polling, load]);

  if (!order) {
    if (!error) {
      return (
        <View style={styles.scroll} testID="order-detail">
          <View style={styles.page}>
            <Text tone="muted">…</Text>
          </View>
        </View>
      );
    }
    const missing = error.status === 404 || error.status === 403;
    return (
      <View style={styles.scroll} testID="order-detail">
        <View style={styles.page} role="main">
          <InlineMessage
            tone="error"
            detail={
              !missing && error.requestId
                ? `Reference: ${error.requestId}`
                : null
            }
            action={
              missing ? undefined : (
                <Button variant="outline" onPress={() => void load()}>
                  {C.retry}
                </Button>
              )
            }
          >
            {missing ? C.notAvailable : C.retry}
          </InlineMessage>
          <Link href="/orders" asChild>
            <Button variant="outline" accessibilityRole="link">
              {C.back}
            </Button>
          </Link>
        </View>
      </View>
    );
  }

  const a = order.address;
  return (
    <ScrollView
      style={styles.scroll}
      testID="order-detail"
      // Keyboard users can scroll an order that has no links or buttons (axe scrollable-region).
      tabIndex={0}
    >
      <View style={styles.page} role="main">
        <Text variant="display" accessibilityRole="header" aria-level={1}>
          {C.orderTitle("")}
          {/* The title is set in lower case; an order number keeps its written case. */}
          <RNText style={styles.asWritten}>{order.number}</RNText>
        </Text>
        <Text weight="semiBold">{STATUS_LABELS[order.status]}</Text>
        <Text tone="muted">
          {C.placedOn(
            istDate(order.placedAt),
            methodLabel(order.paymentMethod),
          )}
        </Text>
        {order.placedByRole === "SCHOOL" ? (
          <Text tone="muted">{C.placedBySchool(order.schoolName)}</Text>
        ) : null}

        {order.note ? (
          <InlineMessage tone="warning">{order.note}</InlineMessage>
        ) : null}
        {order.refundStatus === "REFUNDED" && order.refundedOn ? (
          <InlineMessage tone="info">
            {C.refunded(istDate(`${order.refundedOn}T06:30:00Z`))}
          </InlineMessage>
        ) : order.refundStatus &&
          order.refundAmountInPaise !== null &&
          order.refundAmountInPaise !== undefined ? (
          <InlineMessage tone="info">
            {C.refundDue(formatPaise(order.refundAmountInPaise))}
          </InlineMessage>
        ) : null}

        {order.deliveryCode ? (
          <View
            style={styles.codeCard}
            testID="delivery-code-card"
            role="region"
            aria-label={C.codeTitle}
          >
            <Text weight="semiBold" style={styles.onCode}>
              {C.codeTitle}
            </Text>
            <Text weight="bold" size="x5l" style={styles.code} selectable>
              {order.deliveryCode}
            </Text>
            <Text size="sm" style={styles.onCode}>
              {C.codeHelp}
            </Text>
          </View>
        ) : null}

        {order.driver ? (
          <View
            style={styles.card}
            testID="driver-card"
            role="region"
            aria-label={C.driverRole}
          >
            <Text weight="bold">{order.driver.firstName}</Text>
            <Text size="sm" tone="muted">
              {C.driverRole}
            </Text>
            <Button
              disabled={!order.driver.mobile}
              onPress={() =>
                void Linking.openURL(`tel:${order.driver!.mobile}`)
              }
              testID="call-driver"
            >
              {C.call(order.driver.firstName)}
            </Button>
          </View>
        ) : null}

        <View style={styles.card} role="region" aria-label={C.timeline}>
          <Text weight="bold">{C.timeline}</Text>
          {timelineSteps(order).map((s) => (
            <View
              key={s.status}
              style={styles.step}
              testID={`step-${s.status}`}
            >
              <View
                style={[
                  styles.dot,
                  s.state === "done" && styles.dotDone,
                  s.state === "current" && styles.dotCurrent,
                ]}
              />
              <Text
                style={styles.grow}
                weight={s.state === "current" ? "bold" : "regular"}
                tone={s.state === "todo" ? "muted" : "default"}
              >
                {s.label}
              </Text>
              {s.at ? (
                <Text size="sm" tone="muted">
                  {istDateTime(s.at)}
                </Text>
              ) : null}
            </View>
          ))}
        </View>

        <View style={styles.card} role="region" aria-label={C.itemsTitle}>
          <Text weight="bold">{C.itemsTitle}</Text>
          {order.children.map((c) => (
            <View key={c.studentId} style={styles.card}>
              <Text weight="semiBold">{`${c.name} · ${classSectionText(c.classLevel, c.section)}`}</Text>
              {c.lines.map((l) => (
                <View key={l.productId} style={styles.row}>
                  <Text
                    style={styles.grow}
                  >{`${l.productName} × ${l.quantity}`}</Text>
                  <Text>{formatPaise(l.lineTotalInPaise)}</Text>
                </View>
              ))}
            </View>
          ))}
          <View style={styles.row}>
            <Text>{C.subtotal}</Text>
            <Text>{formatPaise(order.itemsTotalInPaise)}</Text>
          </View>
          <View style={styles.row}>
            <Text>{C.delivery}</Text>
            <Text>
              {order.deliveryFeeInPaise
                ? formatPaise(order.deliveryFeeInPaise)
                : C.free}
            </Text>
          </View>
          <View style={styles.row}>
            <Text weight="bold">{C.total}</Text>
            <Text weight="bold">{formatPaise(order.totalInPaise)}</Text>
          </View>
        </View>

        <View style={styles.card} role="region" aria-label={C.address}>
          <Text weight="bold">{C.address}</Text>
          <Text>{addressLabelText(a.label)}</Text>
          <Text tone="muted">
            {[a.flatHouse, a.streetArea, a.landmark, a.city, a.pincode]
              .filter(Boolean)
              .join(", ")}
          </Text>
        </View>

        {order.linkedOrders.length > 0 ? (
          <View style={styles.card} role="region" aria-label={C.linked}>
            <Text weight="bold">{C.linked}</Text>
            {order.linkedOrders.map((l) => (
              <Link
                key={l.id}
                href={{
                  pathname: "/orders/[orderId]",
                  params: { orderId: l.id },
                }}
              >
                <Text
                  tone="primary"
                  weight="semiBold"
                >{`#${l.number} · ${l.schoolName}`}</Text>
              </Link>
            ))}
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
